import api from './api';

const expenseService = {
  // Get all expenses with filters
  getAllExpenses: async (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.project_id) params.append('project_id', filters.project_id);
    if (filters.budget_id) params.append('budget_id', filters.budget_id);
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.expense_type) params.append('expense_type', filters.expense_type);
    if (filters.status) params.append('status', filters.status);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/expenses?${params.toString()}`);
    return response.data;
  },

  // Get single expense
  getExpense: async (id) => {
    const response = await api.get(`/expenses/${id}`);
    return response.data;
  },

  // Create expense
  createExpense: async (data) => {
    const response = await api.post('/expenses', data);
    return response.data;
  },

  // Update expense
  updateExpense: async (id, data) => {
    const response = await api.put(`/expenses/${id}`, data);
    return response.data;
  },

  // Delete expense
  deleteExpense: async (id) => {
    const response = await api.delete(`/expenses/${id}`);
    return response.data;
  },

  // Approve expense
  approveExpense: async (id, approvedBy) => {
    const response = await api.put(`/expenses/${id}/approve`, { approved_by: approvedBy });
    return response.data;
  },

  // Reject expense
  rejectExpense: async (id, approvedBy) => {
    const response = await api.put(`/expenses/${id}/reject`, { approved_by: approvedBy });
    return response.data;
  },

  // Helper: Get expense types
  getExpenseTypes: () => [
    { value: 'Travel', label: 'Travel', icon: '✈️' },
    { value: 'Materials', label: 'Materials', icon: '📦' },
    { value: 'Software', label: 'Software', icon: '💻' },
    { value: 'Services', label: 'Services', icon: '🔧' },
    { value: 'Other', label: 'Other', icon: '📋' }
  ],

  // Helper: Get expense statuses
  getStatuses: () => [
    { value: 'Pending', label: 'Pending', color: 'badge-warning' },
    { value: 'Approved', label: 'Approved', color: 'badge-success' },
    { value: 'Rejected', label: 'Rejected', color: 'badge-danger' },
    { value: 'Paid', label: 'Paid', color: 'badge-info' }
  ],

  // Helper: Get status color
  getStatusColor: (status) => {
    const colors = {
      Pending: 'badge-warning',
      Approved: 'badge-success',
      Rejected: 'badge-danger',
      Paid: 'badge-info'
    };
    return colors[status] || 'badge-secondary';
  },

  // Helper: Get status badge class
  getStatusBadgeClass: (status) => {
    const classes = {
      Pending: 'badge-warning',
      Approved: 'badge-success',
      Rejected: 'badge-danger',
      Paid: 'badge-info'
    };
    return classes[status] || 'badge-secondary';
  },

  // Helper: Get expense type color
  getTypeColor: (type) => {
    const colors = {
      Travel: 'badge-info',
      Materials: 'badge-success',
      Software: 'badge-purple',
      Services: 'badge-orange',
      Other: 'badge-secondary'
    };
    return colors[type] || 'badge-secondary';
  },

  // Helper: Get expense type icon
  getTypeIcon: (type) => {
    const icons = {
      Travel: '✈️',
      Materials: '📦',
      Software: '💻',
      Services: '🔧',
      Other: '📋'
    };
    return icons[type] || '📋';
  },

  // Helper: Format currency
  formatCurrency: (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  },

  // Helper: Format date
  formatDate: (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  // Helper: Get currencies
  getCurrencies: () => [
    { value: 'USD', label: 'USD - US Dollar', symbol: '$' },
    { value: 'EUR', label: 'EUR - Euro', symbol: '€' },
    { value: 'GBP', label: 'GBP - British Pound', symbol: '£' },
    { value: 'INR', label: 'INR - Indian Rupee', symbol: '₹' },
    { value: 'JPY', label: 'JPY - Japanese Yen', symbol: '¥' },
    { value: 'CAD', label: 'CAD - Canadian Dollar', symbol: 'C$' },
    { value: 'AUD', label: 'AUD - Australian Dollar', symbol: 'A$' }
  ],

  // Helper: Get date range presets
  getDateRangePresets: () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    const startOfYear = new Date(today.getFullYear(), 0, 1);

    return {
      'this-month': {
        label: 'This Month',
        start_date: startOfMonth.toISOString().split('T')[0],
        end_date: endOfMonth.toISOString().split('T')[0]
      },
      'last-month': {
        label: 'Last Month',
        start_date: startOfLastMonth.toISOString().split('T')[0],
        end_date: endOfLastMonth.toISOString().split('T')[0]
      },
      'this-year': {
        label: 'This Year',
        start_date: startOfYear.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      },
      'last-30-days': {
        label: 'Last 30 Days',
        start_date: new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
      }
    };
  }
};

export default expenseService;


